import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { loadState, resetIfNewDay, clearAllProgress } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function StatsPage() {
  const state = resetIfNewDay(loadState());
  const totalAnswered = state.totalAnswered;
  const totalCorrect = state.totalCorrect;
  const accuracy =
    totalAnswered === 0 ? 0 : Math.round((totalCorrect / totalAnswered) * 100);

  const chartData = useMemo(() => {
    // Show last 14 days
    const items = [...state.history].slice(-14);
    if (state.today && state.today.index > 0) {
      items.push({
        date: state.today.date,
        correct: state.today.correct,
        total: state.today.index,
      });
    }
    return items.map((d) => {
      const dt = new Date(d.date);
      const label = dt.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      return { ...d, label };
    });
  }, [state]);

  const handleReset = () => {
    clearAllProgress();
    toast.success("All progress cleared");
    setTimeout(() => window.location.reload(), 600);
  };

  return (
    <section data-testid="stats-page">
      <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B]">
        Your performance
      </div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A] leading-none mt-1">
        Stats
      </h1>

      <div className="grid grid-cols-2 gap-3 mt-8">
        <StatBox
          label="Answered"
          value={totalAnswered}
          testId="stats-total-answered"
        />
        <StatBox
          label="Correct"
          value={totalCorrect}
          testId="stats-total-correct"
        />
        <StatBox
          label="Accuracy"
          value={`${accuracy}%`}
          testId="stats-accuracy"
        />
        <StatBox
          label="Days played"
          value={state.history.length + (state.today?.index > 0 ? 1 : 0)}
          testId="stats-days-played"
        />
      </div>

      <div className="mt-10 border border-[#E4E4E7] rounded-md bg-white p-4">
        <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-3 px-1">
          Daily history
        </div>
        {chartData.length === 0 ? (
          <div
            className="py-10 text-center text-sm text-[#A1A1AA]"
            data-testid="stats-history-empty"
          >
            No history yet — answer some questions to see your daily chart here.
          </div>
        ) : (
          <div className="h-52" data-testid="stats-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 8, left: -24, bottom: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#E4E4E7"
                  strokeDasharray="2 3"
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#52525B" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 10]}
                  tick={{ fontSize: 10, fill: "#52525B" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "#F4F4F5" }}
                  contentStyle={{
                    border: "1px solid #E4E4E7",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  formatter={(value, name) => [value, name === "correct" ? "Correct" : name]}
                />
                <Bar dataKey="correct" fill="#0A0A0A" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="mt-10">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="text-sm text-[#52525B] underline underline-offset-4 hover:text-[#DC2626]"
              data-testid="reset-progress-button"
            >
              Reset all progress
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset all progress?</AlertDialogTitle>
              <AlertDialogDescription>
                This will clear your daily history, totals, and today&apos;s session.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReset}
                data-testid="reset-confirm"
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              >
                Yes, reset
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </section>
  );
}

function StatBox({ label, value, testId }) {
  return (
    <div className="border border-[#E4E4E7] rounded-md bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#52525B]">
        {label}
      </div>
      <div
        className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A] mt-2 tabular-nums"
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}
